/**
 * Note Sections List
 *
 * Container component that renders all note sections with:
 * - Loading states
 * - Empty states
 * - Section highlighting for AI changes
 * - Streaming updates
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { NoteSection } from '@features/notes';
import { Box, Typography } from '@mui/material';

import type { AnyBlockNoteEditor } from '../../../utils/blocknote/types';
import type { UseApprovalNavigationReturn } from '../hooks/useApprovalNavigation';
import type { EditPreview } from '../types';

import { NoteSectionContainer } from './layouts/NoteSectionContainer';
import { NoteSectionItem } from './NoteSectionItem';

interface NoteSectionsListProps {
  /**
   * Array of note sections
   */
  sections: NoteSection[];

  /**
   * IDs of sections affected by pending approval
   */
  affectedSectionIds?: string[];

  /**
   * Map of section IDs to pending AI-generated content
   */
  sectionUpdates?: Record<string, string>;

  /**
   * Array of section IDs that are currently being streamed
   */
  streamingSections?: string[];

  /**
   * Array of edit previews with diff data (for affected sections)
   * Contains original_text, edited_text, and diff operations
   */
  edits?: EditPreview[];

  /**
   * Callback when section content changes
   * Returns a Promise that resolves when the save completes
   */
  onSectionChange: (index: number, editedText: string) => Promise<void>;

  /**
   * Encounter ID for analytics metadata
   */
  encounterId?: string;

  /**
   * Patient the note belongs to. Chart-backed sections (`section.kind`) need it
   * to read and write the live chart; ordinary text sections ignore it.
   */
  patientId?: string;

  /**
   * Whether sections should be editable
   */
  editable?: boolean;

  /**
   * Callback when user approves the AI suggestion (dismisses affected state)
   */
  onApprove?: (sectionId: string) => void;

  /**
   * Callback when user reverts the AI suggestion
   */
  onRevert?: (sectionId: string) => void;

  /**
   * Map of section IDs to their previous content (before AI edit)
   */
  previousContent?: Record<string, string>;

  /**
   * Navigation functions and state from useApprovalNavigation hook
   */
  navigation?: UseApprovalNavigationReturn;

  /**
   * Callback when a save operation starts (for navigation guard)
   * Receives the section ID and save promise to track completion
   */
  onSaveStart?: (sectionId: string, savePromise: Promise<void>) => void;

  /**
   * Callback when section content is modified (marks as dirty)
   */
  onMarkDirty?: (sectionId: string) => void;

  /**
   * Map of section_id to audit issue count (warnings + non-compliant)
   * Computed from RTK Query audit data and passed down
   */
  sectionIssueMap?: Record<string, number>;

  /**
   * Streaming sections from note generation v2 workflow
   * Used to display sections as they are generated in real-time
   */
  generationStreamingSections?: Array<{
    id: string;
    title: string;
    text: string;
  }>;

  /**
   * Whether note generation is in progress (v2 streaming flow)
   * When true, displays streaming sections with loading indicators
   */
  isGenerating?: boolean;

  /**
   * Callback when BlockNote editor registry is updated
   * Allows parent to access editor instances for copy operations
   */
  onEditorsReady?: (editorsMap: Map<string, AnyBlockNoteEditor>) => void;
}

export const NoteSectionsList: React.FC<NoteSectionsListProps> = ({
  sections,
  affectedSectionIds = [],
  sectionUpdates = {},
  streamingSections = [],
  edits,
  onSectionChange,
  encounterId,
  patientId,
  editable = true,
  onApprove,
  onRevert,
  previousContent = {},
  navigation,
  onSaveStart,
  onMarkDirty,
  sectionIssueMap = {},
  generationStreamingSections,
  isGenerating = false,
  onEditorsReady,
}) => {
  // Memoize affected section IDs as a Set for O(1) lookup
  const affectedSet = useMemo(() => new Set(affectedSectionIds), [affectedSectionIds]);

  // Memoize streaming section IDs as a Set for O(1) lookup
  const streamingSet = useMemo(() => new Set(streamingSections), [streamingSections]);

  // ============================================================================
  // BLOCKNOTE EDITOR REGISTRY
  // ============================================================================
  // Store BlockNote editor instances for copy operations (follows JotDocs pattern)
  const blockNoteEditorsRef = useRef<Map<string, AnyBlockNoteEditor>>(new Map());

  // Callback to register/unregister editor instances from NoteSectionItem components
  const handleEditorReady = useCallback(
    (sectionId: string, editor: AnyBlockNoteEditor | null) => {
      if (editor) {
        blockNoteEditorsRef.current.set(sectionId, editor);
      } else {
        blockNoteEditorsRef.current.delete(sectionId);
      }
      // Notify parent of updated editor registry
      if (onEditorsReady) {
        onEditorsReady(new Map(blockNoteEditorsRef.current));
      }
    },
    [onEditorsReady],
  );

  // Cleanup: Clear editor registry on unmount
  useEffect(() => {
    return () => {
      blockNoteEditorsRef.current.clear();
    };
  }, []);

  // Create a set of generation streaming section IDs for O(1) lookup
  const generationStreamingSet = useMemo(
    () => new Set(generationStreamingSections?.map(s => s.id) || []),
    [generationStreamingSections],
  );

  // Determine which sections to display:
  // - If generating AND we have streaming data OR if we have no real sections yet but have streaming data
  // - Otherwise, show regular sections
  // NOTE: We need to check for actual streaming data, not just isGenerating flag,
  // because isGenerating might be true before SSE has returned any sections
  const hasRealSections = sections && sections.length > 0;
  const hasStreamingData = generationStreamingSections && generationStreamingSections.length > 0;
  const hasGenerationSections = (isGenerating && hasStreamingData) || (!hasRealSections && hasStreamingData);

  // ============================================================================
  // MERGE SECTIONS FOR RENDERING
  // ============================================================================
  // When generating, merge streaming sections with loaded sections
  // Priority: loaded sections take precedence, streaming sections fill gaps
  // NOTE: This useMemo must be called BEFORE any early returns to satisfy React's rules of hooks
  const displaySections: Array<
    NoteSection | { section_id: string; name: string; text: string; isGenerationStreaming: true }
  > = useMemo(() => {
    // If not generating or no streaming sections, return regular sections
    if (!hasGenerationSections || !generationStreamingSections) {
      return sections;
    }

    // Create a map of loaded sections by ID
    const loadedSectionsMap = new Map(sections.map(s => [s.section_id, s]));

    // Build display list: prefer loaded sections, fall back to streaming
    const result: Array<
      NoteSection | { section_id: string; name: string; text: string; isGenerationStreaming: true }
    > = [];
    const addedIds = new Set<string>();

    // Add streaming sections (they come in order from the stream)
    for (const streamingSection of generationStreamingSections) {
      const loadedSection = loadedSectionsMap.get(streamingSection.id);
      if (loadedSection) {
        // Use loaded section if available
        result.push(loadedSection);
      } else {
        // Use streaming section
        result.push({
          section_id: streamingSection.id,
          name: streamingSection.title,
          text: streamingSection.text,
          isGenerationStreaming: true,
        });
      }
      addedIds.add(streamingSection.id);
    }

    // Add any loaded sections not in streaming (in case stream is incomplete)
    for (const section of sections) {
      if (!addedIds.has(section.section_id)) {
        result.push(section);
      }
    }

    return result;
  }, [sections, hasGenerationSections, generationStreamingSections]);

  // Empty state - show only if no sections AND not generating with streaming sections
  if ((!sections || sections.length === 0) && !hasGenerationSections) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 4,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" color="textSecondary" gutterBottom>
          {isGenerating ? 'Generating Note...' : 'No Sections Found'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {isGenerating
            ? 'Sections will appear here as they are generated.'
            : "This note doesn't have any sections yet. Start a chat to generate content."}
        </Typography>
      </Box>
    );
  }

  return (
    <NoteSectionContainer
      sx={{
        py: 6,
        flex: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
      }}
    >
      {displaySections.map((section, index) => {
        const sectionId = section.section_id;
        const isGenerationStreaming =
          'isGenerationStreaming' in section && section.isGenerationStreaming;
        const isAffected = affectedSet.has(sectionId);
        const pendingContent = sectionUpdates[sectionId];
        // Only mark as streaming during active AI writing or generation streaming
        const isActivelyStreaming =
          streamingSet.has(sectionId) || generationStreamingSet.has(sectionId);
        const isStreaming = isActivelyStreaming || isGenerationStreaming;

        // For generation streaming sections, create a compatible NoteSection object
        // NoteSection only requires: section_id, name, text
        const displaySection: NoteSection = isGenerationStreaming
          ? {
              section_id: section.section_id,
              name: section.name,
              text: section.text,
            }
          : (section as NoteSection);

        return (
          <NoteSectionItem
            key={sectionId}
            section={displaySection}
            index={index}
            isAffected={isAffected}
            isStreaming={isStreaming}
            pendingContent={pendingContent}
            edits={edits}
            onChange={onSectionChange}
            encounterId={encounterId}
            patientId={patientId}
            editable={editable && !isGenerationStreaming}
            onApprove={onApprove ? () => onApprove(sectionId) : undefined}
            onRevert={onRevert ? () => onRevert(sectionId) : undefined}
            previousContent={previousContent[sectionId]}
            onSaveStart={onSaveStart}
            onMarkDirty={onMarkDirty}
            sectionIssueCount={sectionIssueMap[sectionId] || 0}
            onEditorReady={editor => handleEditorReady(sectionId, editor)}
          />
        );
      })}
    </NoteSectionContainer>
  );
};
